import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardWrapperSpecific } from "../CardWrapperSpecific";
import { setPaymentChoice } from "../../store";
import { SupportCodeModal } from "@pages/Applications/DeviceAssociateModal/MoneticsApplicationForm/SupportCodeModal";
import { ConfigModal } from "./ConfigModal";
import { usePaymentConfigs } from "./hooks";
import "./index.css";

type PaymentChoiceItem = {
    name: string;
    active: boolean;
    image: string;
};

const PAYMENT_PROVIDER = {
    PAYLINE: "payline",
    CLICK_TO_PAY: "clicktopay",
    YAVIN: "yavin",
} as const;

type PaymentProvider = typeof PAYMENT_PROVIDER[keyof typeof PAYMENT_PROVIDER];

function getInitialItems(t: any, selectedRows: any): PaymentChoiceItem[] {
    const firstRow = selectedRows[0];
    return [
        {
            name: t("Credit card"),
            active: firstRow?.["Credit card"] ?? false,
            image: "./images/byCreditCard.png",
        },
        {
            name: t("At the counter"),
            active: firstRow?.["At the counter"] ?? false,
            image: "./images/atTheCounter.png",
        },
    ];
}

export default function PaymentChoice({ selectedRows }: any) {
    const { t } = useTranslation();
    const [items, setItems] = useState<PaymentChoiceItem[]>(() =>
        getInitialItems(t, selectedRows)
    );

    const [isSupportCodeOpened, setIsSupportCodeOpened] = useState(false);
    const [isConfigModalOpened, setIsConfigModalOpened] = useState(false);
    const [codeSupport, setCodeSupport] = useState("");
    const [isValidCode, setIsValidCode] = useState(false);

    const isCreditCardActive = items[0]?.active === true;

    const {
        isPayline,
        isClickToPay,
        isYavin,
        hasConfigurableProvider,
        paylineConfig,
        clickToPayConfig,
        yavinConfig,
        savePayline,
        saveClickToPay,
        saveYavin,
    } = usePaymentConfigs({ selectedRows, isCreditCardActive });

    useEffect(() => {
        setPaymentChoice(items);
    }, [items]);

    const canConfigureCreditCard =
        isCreditCardActive && hasConfigurableProvider;

    const providerFlags: Array<[boolean, PaymentProvider]> = [
        [isPayline, PAYMENT_PROVIDER.PAYLINE],
        [isClickToPay, PAYMENT_PROVIDER.CLICK_TO_PAY],
        [isYavin, PAYMENT_PROVIDER.YAVIN],
    ];

    const activeProvider: PaymentProvider | null =
        providerFlags.find(([isActive]) => isActive)?.[1] ?? null;

    return (
        <React.Fragment>
            <h2
                data-testid="payment-choice-title"
                className="ml-5 pt-3 payment-choice-title__clz"
            >
                {t("Payment choice")}
            </h2>

            <div
                data-testid="payment-choice-container"
                className="payment-choice-container__clz"
            >
                <div
                    data-testid="payment-choice-cards"
                    className="payment-choice-cards__clz"
                >
                    {items.map((item, index) => (
                        <CardWrapperSpecific
                            items={items}
                            index={index}
                            setItems={setItems}
                            disabled={false}
                            key={index}
                            showSettings={
                                item.name === t("Credit card") &&
                                canConfigureCreditCard
                            }
                            onSettingsClick={() => setIsSupportCodeOpened(true)}
                        />
                    ))}
                </div>
            </div>

            <SupportCodeModal
                isModalOpened={isSupportCodeOpened}
                setIsModalOpened={setIsSupportCodeOpened}
                codeSupport={codeSupport}
                setCodeSupport={setCodeSupport}
                isValidCode={isValidCode}
                setIsValidCode={setIsValidCode}
                isConfig
                setIsModalConfigOpened={setIsConfigModalOpened}
                title={t("Online payment")}
            />

            <ConfigModal
                isOpen={isConfigModalOpened}
                onClose={() => setIsConfigModalOpened(false)}
                provider={activeProvider}
                paylineConfig={paylineConfig}
                clickToPayConfig={clickToPayConfig}
                yavinConfig={yavinConfig}
                onPaylineSave={savePayline}
                onClickToPaySave={saveClickToPay}
                onYavinSave={saveYavin}
            />
        </React.Fragment>
    );
}
